import React, { useCallback, useMemo } from 'react';
import { DecoratorNode, $getNodeByKey } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useTranslation } from 'react-i18next';

const sanitizeRows = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => ({
    label: row?.label || '',
    value: row?.value || '',
  }));
};

const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      title: '',
      subtitle: '',
      referenceLine: '',
      client: '',
      date: '',
      backgroundUrl: '',
      metadataRows: [],
      essentialRows: [],
      extendedRows: [],
      metadataSectionTitle: '',
    };
  }
  const metadataRows = sanitizeRows(data.metadataRows);
  const essentialRows = sanitizeRows(data.essentialRows);
  const extendedRows = sanitizeRows(data.extendedRows);
  return {
    title: data.title || '',
    subtitle: data.subtitle || '',
    referenceLine: data.referenceLine || '',
    client: data.client || '',
    date: data.date || '',
    backgroundUrl: data.backgroundUrl || '',
    metadataRows,
    essentialRows: essentialRows.length ? essentialRows : metadataRows,
    extendedRows,
    metadataSectionTitle: data.metadataSectionTitle || '',
  };
};

function EditableText({ value, onChange, readOnly, className, multiline = false, placeholder = '' }) {
  if (readOnly) {
    return <div className={className}>{value || ''}</div>;
  }
  if (multiline) {
    return (
      <textarea
        className={`${className} report-draft-editor__cover-input report-draft-editor__cover-input--editable`}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={2}
        spellCheck={false}
      />
    );
  }
  return (
    <input
      type="text"
      className={`${className} report-draft-editor__cover-input report-draft-editor__cover-input--editable`}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      spellCheck={false}
    />
  );
}

function ReportCoverComponent({ nodeKey, data }) {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const readOnly = !editor.isEditable();
  const nodeData = useMemo(() => sanitizeData(data), [data]);
  const clientLabel = t('reports.review.template.cover.fields.client', { defaultValue: 'Cliente' });
  const dateLabel = t('reports.review.template.cover.fields.documentDate', { defaultValue: 'Data' });

  const updateData = useCallback(
    (updater) => {
      if (readOnly) {
        return;
      }
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isReportCoverNode(node)) {
          return;
        }
        const current = sanitizeData(node.getData());
        const nextData = typeof updater === 'function' ? updater(current) : updater;
        node.replaceData(sanitizeData(nextData));
      });
    },
    [editor, nodeKey, readOnly]
  );

  const updateField = useCallback(
    (fieldName, value) => {
      updateData((current) => ({
        ...current,
        [fieldName]: value,
      }));
    },
    [updateData]
  );

  const hasBackground = Boolean(nodeData.backgroundUrl);

  return (
    <section
      className="report-draft-editor__cover-node"
      contentEditable={false}
      data-readonly={readOnly ? 'true' : 'false'}
    >
      <div className="report-draft-editor__cover-hero">
        {hasBackground ? (
          <div
            className="report-draft-editor__cover-background"
            style={{ backgroundImage: `url(${nodeData.backgroundUrl})` }}
            aria-hidden="true"
          />
        ) : null}
        <div className="report-draft-editor__cover-overlay" />

        <div className="report-draft-editor__cover-header">
          <div className="report-draft-editor__cover-copy">
            <EditableText
              className="report-draft-editor__cover-title"
              value={nodeData.title}
              onChange={(value) => updateField('title', value)}
              readOnly={readOnly}
            />
            <EditableText
              className="report-draft-editor__cover-subtitle"
              value={nodeData.subtitle}
              onChange={(value) => updateField('subtitle', value)}
              readOnly={readOnly}
            />
            <EditableText
              className="report-draft-editor__cover-reference"
              value={nodeData.referenceLine}
              onChange={(value) => updateField('referenceLine', value)}
              readOnly={readOnly}
            />
            <div className="report-draft-editor__cover-client-row">
              <span>{clientLabel}:</span>
              <EditableText
                className="report-draft-editor__cover-client"
                value={nodeData.client}
                onChange={(value) => updateField('client', value)}
                readOnly={readOnly}
              />
            </div>
            <div className="report-draft-editor__cover-date-row">
              <span>{dateLabel}:</span>
              <EditableText
                className="report-draft-editor__cover-date"
                value={nodeData.date}
                onChange={(value) => updateField('date', value)}
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

export class ReportCoverNode extends DecoratorNode {
  __data;

  static getType() {
    return 'report-cover';
  }

  static clone(node) {
    return new ReportCoverNode(node.__data, node.__key);
  }

  static importJSON(serializedNode) {
    return $createReportCoverNode(serializedNode.data || {});
  }

  constructor(data, key) {
    super(key);
    this.__data = sanitizeData(data);
  }

  exportJSON() {
    return {
      type: 'report-cover',
      version: 1,
      data: this.__data,
    };
  }

  createDOM() {
    const dom = document.createElement('div');
    dom.className = 'report-draft-editor__cover-node-wrap';
    return dom;
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return <ReportCoverComponent nodeKey={this.getKey()} data={this.__data} />;
  }

  isInline() {
    return false;
  }

  isIsolated() {
    return true;
  }

  getData() {
    return sanitizeData(this.getLatest().__data);
  }

  replaceData(nextData) {
    const writable = this.getWritable();
    writable.__data = sanitizeData(nextData);
  }
}

export function $createReportCoverNode(data) {
  return new ReportCoverNode(data);
}

export function $isReportCoverNode(node) {
  return node instanceof ReportCoverNode;
}
